// Products Table Component - Display EOL/EOS products with actions

import React, { useState } from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Stack,
  IconButton,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  Text,
  TooltipHost,
  mergeStyleSets,
  useTheme,
} from '@fluentui/react';
import type { IColumn } from '@fluentui/react';
import type { EndOfLifeProduct } from '../types';
import { formatDate, getProductStatus, getStatusColor, getRelativeDate } from '../utils/formatters';

interface ProductsTableProps {
  products: EndOfLifeProduct[];
  onEditProduct: (product: EndOfLifeProduct) => void;
  onDeleteProduct: (productId: number) => void;
  onManageRelations: (product: EndOfLifeProduct) => void;
  canWrite: boolean;
}

export const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  onEditProduct,
  onDeleteProduct,
  onManageRelations,
  canWrite,
}) => {
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<EndOfLifeProduct | null>(null);

  const styles = mergeStyleSets({
    statusBadge: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600,
      color: '#fff',
    },
  });

  const handleDeleteClick = (product: EndOfLifeProduct) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      onDeleteProduct(productToDelete.id);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const columns: IColumn[] = [
    {
      key: 'manufacturer',
      name: 'Manufacturer',
      fieldName: 'manufactuerName',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => (
        <Text>{item.manufactuerName || 'N/A'}</Text>
      ),
    },
    {
      key: 'productName',
      name: 'Product Name',
      fieldName: 'productName',
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => (
        <TooltipHost content={item.productDescription || item.productName}>
          <Text>{item.productName || 'N/A'}</Text>
        </TooltipHost>
      ),
    },
    {
      key: 'productNumber',
      name: 'Product #',
      fieldName: 'productNumber',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => (
        <Text>{item.productNumber || 'N/A'}</Text>
      ),
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 100,
      maxWidth: 120,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => {
        const status = getProductStatus(item.endOfSaleDate, item.endOfSupportDate);
        const color = getStatusColor(status);
        return (
          <span className={styles.statusBadge} style={{ backgroundColor: color }}>
            {status}
          </span>
        );
      },
    },
    {
      key: 'endOfSaleDate',
      name: 'End of Sale',
      fieldName: 'endOfSaleDate',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => {
        const relative = getRelativeDate(item.endOfSaleDate, 'EOL');
        return (
          <TooltipHost content={relative}>
            <Text>{formatDate(item.endOfSaleDate)}</Text>
          </TooltipHost>
        );
      },
    },
    {
      key: 'endOfSupportDate',
      name: 'End of Support',
      fieldName: 'endOfSupportDate',
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: EndOfLifeProduct) => {
        const relative = getRelativeDate(item.endOfSupportDate, 'EOS');
        return (
          <TooltipHost content={relative}>
            <Text>{formatDate(item.endOfSupportDate)}</Text>
          </TooltipHost>
        );
      },
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 140,
      maxWidth: 140,
      isResizable: false,
      onRender: (item: EndOfLifeProduct) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
          {canWrite && (
            <>
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title="Edit Product"
                ariaLabel="Edit Product"
                onClick={() => onEditProduct(item)}
              />
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Delete Product"
                ariaLabel="Delete Product"
                onClick={() => handleDeleteClick(item)}
              />
            </>
          )}
          <IconButton
            iconProps={{ iconName: 'Link' }}
            title="Manage Relations"
            ariaLabel="Manage Product Relations"
            onClick={() => onManageRelations(item)}
          />
        </Stack>
      ),
    },
  ];

  return (
    <>
      <DetailsList
        items={products}
        columns={columns}
        setKey="set"
        layoutMode={DetailsListLayoutMode.justified}
        selectionMode={SelectionMode.none}
        isHeaderVisible={true}
        ariaLabelForGrid="End of Life and End of Support products table"
        ariaLabel="Products list with manufacturer, product name, and lifecycle dates"
        styles={{
          root: {
            backgroundColor: theme.palette.white,
          },
        }}
      />

      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={handleCancelDelete}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Confirm Delete',
          subText: `Are you sure you want to delete "${productToDelete?.productName}"? This action cannot be undone.`,
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={handleConfirmDelete} text="Delete" />
          <DefaultButton onClick={handleCancelDelete} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </>
  );
};
